import { lineStrings } from 'constants/strings'
import {
  ERichMenuAreaActionType,
  LINE_RICH_MENU_IMAGE_MAX_BYTES,
  RICH_MENU_ACTION_LABEL_MAX_LENGTH,
  RICH_MENU_CHAT_BAR_TEXT_MAX_LENGTH,
  RICH_MENU_MAILTO_ENCODED_MAX_LENGTH,
  RICH_MENU_MESSAGE_ACTION_TEXT_MAX_LENGTH,
  RICH_MENU_NAME_MAX_LENGTH,
  RICH_MENU_URI_MAX_LENGTH,
  type TRichMenuAreaAction,
} from 'types'
import {
  isMailtoUri,
  MAILTO_SCHEME,
  parseMailtoUri,
} from 'utils/richMenu'

const msgs = lineStrings.management.richMenu.create.errors

/**
 * LINE Messaging API — URI action の scheme（公式ドキュメント）
 * LINEが直接受け付けるスキームだけを持つ汎用の集合で、リッチメッセージからも共有される。
 * mailto: を含めてはならない — リッチメッセージのタップ先はBAN誘導ラップURLを通らず
 * そのまま imagemap の linkUri になるため、管理画面では保存できてもLINE適用で失敗する。
 * メール導線はリッチメニュー側の `isMailtoUri` 分岐だけで受け付ける。
 * @see https://developers.line.biz/en/reference/messaging-api/#uri-action
 */
const ALLOWED_URI_PROTOCOLS = new Set(['http:', 'https:', 'line:', 'tel:'])

/**
 * ドメインの1ラベル。先頭・末尾のハイフンを許さず、63文字を超えない（RFC 1035 §2.3.1）。
 * 否定lookbehindはSafariの対応バージョンに依存するため、ラベル単位で書き下している。
 */
const DNS_LABEL = '[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?'

/**
 * メールアドレスの形式（バックエンドの line-uri-action.validator と同一の集合）。
 * mailto: URIへそのまま埋め込んでも曖昧にならない文字だけを許可する。
 * `#` `?` `%` `&` などのURI予約文字はfragment/queryとして解釈されるため弾く。
 * 先頭・末尾・連続のドットも許可しない。
 * 双方のテストで同一のvalid/invalidベクタを共有し、片側だけ緩むのを防いでいる。
 */
const MAIL_ADDRESS_PATTERN = new RegExp(
  `^[A-Za-z0-9_+-]+(?:\\.[A-Za-z0-9_+-]+)*@${DNS_LABEL}(?:\\.${DNS_LABEL})+$`,
)

export type TRichMenuAreaFieldErrors = {
  uri?: string
  /** メールの件名・本文に対するエラー（宛先欄ではなくそちらに出す）。 */
  mailContent?: string
  text?: string
  label?: string
  surveyId?: string
}

export type TRichMenuCreateValidationResult = {
  ok: boolean
  areaErrors: Record<number, TRichMenuAreaFieldErrors>
  nameError?: string
  chatBarError?: string
  imageError?: string
  firstErrorAreaIndex?: number
}

export function isValidRichMenuUri(value: string): boolean {
  const trimmed = value.trim()
  if (!trimmed) return false
  if (value.length > RICH_MENU_URI_MAX_LENGTH) return false
  try {
    const u = new URL(trimmed)
    return ALLOWED_URI_PROTOCOLS.has(u.protocol.toLowerCase())
  } catch {
    return false
  }
}

/** メールアドレスとして受け付けられる形式かを判定する。 */
export function isValidMailAddress(value: string): boolean {
  return MAIL_ADDRESS_PATTERN.test(value.trim())
}

/**
 * mailto: をBAN誘導ラップURLに包んでもLINEのURI上限に収まるかを判定する。
 * 日本語の件名・本文はpercent-encodeで大きく膨らむため、エンコード後の長さで見る。
 */
export function exceedsMailtoUriLimit(mailtoUri: string): boolean {
  try {
    return (
      encodeURIComponent(mailtoUri).length > RICH_MENU_MAILTO_ENCODED_MAX_LENGTH
    )
  } catch {
    // 孤立サロゲート等でエンコードできない値は登録できないため拒否側に倒す
    return true
  }
}

/**
 * メール導線のタップ先を検証する。
 * 長さ超過は件名・本文が原因なので、宛先欄とは別のフィールドへ返す。
 */
function validateMailAction(uri: string): TRichMenuAreaFieldErrors {
  const mail = parseMailtoUri(uri)
  if (!mail) return { uri: msgs.uriInvalid }

  if (!mail.address.trim()) return { uri: msgs.mailAddressRequired }
  if (!isValidMailAddress(mail.address)) return { uri: msgs.mailAddressInvalid }
  if (exceedsMailtoUriLimit(uri)) return { mailContent: msgs.mailContentTooLong }

  return {}
}

export function validateRichMenuCreateForm(params: {
  areas: TRichMenuAreaAction[]
  name: string
  chatBarText: string
  hasImage: boolean
  imageFileSize?: number
}): TRichMenuCreateValidationResult {
  const areaErrors: Record<number, TRichMenuAreaFieldErrors> = {}
  let nameError: string | undefined
  let chatBarError: string | undefined
  let imageError: string | undefined

  if (!params.hasImage) {
    imageError = msgs.imageRequired
  } else if (
    params.imageFileSize !== undefined &&
    params.imageFileSize > LINE_RICH_MENU_IMAGE_MAX_BYTES
  ) {
    imageError = msgs.imageTooLarge
  }

  if (params.name.length > RICH_MENU_NAME_MAX_LENGTH) {
    nameError = msgs.nameTooLong
  }

  if (params.chatBarText.length > RICH_MENU_CHAT_BAR_TEXT_MAX_LENGTH) {
    chatBarError = msgs.chatBarTooLong
  }

  params.areas.forEach((action, index) => {
    const field: TRichMenuAreaFieldErrors = {}

    if (action.type === ERichMenuAreaActionType.Uri) {
      const raw = action.uri ?? ''
      const trimmed = raw.trim()
      if (!trimmed || trimmed === MAILTO_SCHEME) {
        field.uri = isMailtoUri(raw) ? msgs.mailAddressRequired : msgs.uriRequired
      } else if (isMailtoUri(raw)) {
        // メールはURLより厳しい長さ上限を持つため、汎用の長さ判定より先に検証する。
        // undefined を代入すると誤ってエラー扱いになるため、値があるときだけ入れる。
        const mailErrors = validateMailAction(raw)
        if (mailErrors.uri) field.uri = mailErrors.uri
        if (mailErrors.mailContent) field.mailContent = mailErrors.mailContent
      } else if (raw.length > RICH_MENU_URI_MAX_LENGTH) {
        field.uri = msgs.uriTooLong
      } else if (!isValidRichMenuUri(raw)) {
        field.uri = msgs.uriInvalid
      }
      const label = action.label ?? ''
      if (label.length > RICH_MENU_ACTION_LABEL_MAX_LENGTH) {
        field.label = msgs.labelTooLong
      }
    }

    if (action.type === ERichMenuAreaActionType.Message) {
      const text = action.text ?? ''
      const trimmed = text.trim()
      if (!trimmed) {
        field.text = msgs.textRequired
      } else if (text.length > RICH_MENU_MESSAGE_ACTION_TEXT_MAX_LENGTH) {
        field.text = msgs.textTooLong
      }
    }

    if (action.type === ERichMenuAreaActionType.Survey) {
      if (
        !Number.isSafeInteger(action.surveyId) ||
        (action.surveyId ?? 0) < 1
      ) {
        field.surveyId = msgs.surveyRequired
      }
    }

    if (Object.keys(field).length > 0) {
      areaErrors[index] = field
    }
  })

  const ok =
    !imageError &&
    !nameError &&
    !chatBarError &&
    Object.keys(areaErrors).length === 0

  let firstErrorAreaIndex: number | undefined
  if (!ok && Object.keys(areaErrors).length > 0) {
    firstErrorAreaIndex = Math.min(
      ...Object.keys(areaErrors).map((k) => Number.parseInt(k, 10)),
    )
  }

  return {
    ok,
    areaErrors,
    nameError,
    chatBarError,
    imageError,
    firstErrorAreaIndex,
  }
}
